import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, MenuItem, Stack, TextField, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { useSnackbar } from 'notistack'
import { RateCardHeatmap, HeatLegend } from './Tables'
import { Pill, NoteInfoIcon, SectionCard } from './common'
import ProspectAndCommercialSummary from './ProspectAndCommercialSummary'
// Shared review-surface building blocks — one source with DealReview so the
// history modal and the review page never drift apart visually.
import { cols, miniGrid, SecTitle, onBluePill, statLbl, statVal, statSub, btnPri, btnBlue, btnGhost, ActionGroup, RfiMatrix, rfiMatricesFromVersion } from './reviewBits'
import { downloadXlsx, downloadSheets, allocationBdRows, serviceabilityRows, marginWorkingRows, allocationInternalRows, exceptionRows, downloadRateCardMarginWorking } from './fileExports'
import { PATHS, BUCKETS, ZONE_CLASSES } from '../constants'
import { getAccess } from '../../../helpers/ReduxHelpers'
import { openS3Doc, exportPreviewPdf } from '../s3Doc'
import {
  useGetVersionQuery, useGetProposalDocQuery, useSendMutation, useOutcomeMutation,
  useUploadOrderDataMutation, useLazyGetOrderDataTemplateQuery,
} from '../../../redux/d2cApiSlice'
import { lanesToHeatmap, versionToSeed, BUCKET_LABEL_TO_KEY, maskVersionMargins } from '../apiMap'

const TNC = [
  'Rates are exclusive of GST; billed on the higher of dead/volumetric weight.',
  'RTO and COD charges are auto-applied on forward cards per the rate-card library.',
  'Reverse (RVP) is priced off the forward card; QC charge applies per successful pickup.',
  'Fuel surcharge, if applicable, is added at the prevailing rate on the invoice date.',
  'Serviceability and TAT are indicative and subject to courier serviceability at time of booking.',
]

const DOC_STATUSES = ['APPROVED', 'SENT', 'CONVERTED', 'LOST']

/** ProposalDetailModal — full rate card + T&Cs + working files + SL lifecycle actions. */
export default function ProposalDetailModal({ open, onClose, proposal }) {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const versionId = proposal?.versionId
  const proposalId = proposal?.proposalId || proposal?.id
  const status = proposal?.apiStatus
  const safeBrand = (proposal?.brand || 'proposal').replace(/[^\w-]+/g, '_')

  const { data: version, isFetching, isError, error } = useGetVersionQuery(versionId, { skip: !open || !versionId })
  const { data: doc } = useGetProposalDocQuery(proposalId, { skip: !open || !proposalId || !DOC_STATUSES.includes(status) })
  const [send, sendState] = useSendMutation()
  const [uploadOrderData, uploadState] = useUploadOrderDataMutation()
  const [fetchTemplate, templateState] = useLazyGetOrderDataTemplateQuery()
  const [outcome, outcomeState] = useOutcomeMutation()
  const [result, setResult] = useState('CONVERTED')
  const [reason, setReason] = useState('')

  // Mockup v19 modal shows "Working files (6)" for reviewers (their API responses carry
  // cost/margin unmasked); the SL sees only the client-facing 3 (masking side-rule).
  const isReviewer = getAccess().some((a) => a === 'PROPOSAL_L1' || a === 'PROPOSAL_ADMIN')
  // Defence-in-depth (14-Aug review item 3): for non-reviewers, null every margin/cost
  // field BEFORE rendering — even if the backend's role-masking ever slips (e.g. on
  // APPROVED versions), this screen still shows "masked" / "—" everywhere.
  const safeVersion = useMemo(() => (isReviewer ? version : maskVersionMargins(version)), [isReviewer, version])
  const heatmap = useMemo(() => lanesToHeatmap(safeVersion?.computedLanes || []), [safeVersion])
  // Real client-facing pack derived from the engine output (no mock files).
  const lanes = useMemo(() => safeVersion?.computedLanes || [], [safeVersion])
  // computedRtoLanes (D2C RTO Margin Breakout §5) — the RTO breakout, exported as a second
  // "Margin Working (RTO)" tab in the SAME Margin Working file, never a separate download.
  const rtoLanes = useMemo(() => safeVersion?.computedRtoLanes || [], [safeVersion])
  const allocRows = useMemo(() => allocationBdRows(lanes), [lanes])
  const svcRows = useMemo(() => serviceabilityRows(lanes), [lanes])
  const hasPack = !!lanes.length
  // Why downloads may be unavailable — say it, never grey out silently.
  const noLanesReason = !versionId
    ? 'This proposal has no generated version yet — generate it first.'
    : (version && !lanes.length ? 'No pricing lanes were computed on this version — re-generate once courier commercials are available.' : '')
  const slFiles = [
    { ttl: 'Client Proposal', sub: 'Outward rate cards + T&Cs — D2C format', kind: 'doc' },
    ...(isReviewer ? [{
      ttl: 'Internal Margin Working', sub: 'Courier-wise revenue / cost / margin per lane (.xlsx)', kind: 'xlsx',
      rows: marginWorkingRows(lanes), file: `${safeBrand}_Margin_Working.xlsx`, sheet: 'Margin Working',
      ...(rtoLanes.length ? { extraSheets: [{ name: 'Margin Working (RTO)', rows: marginWorkingRows(rtoLanes) }] } : {}),
    }] : []),
    { ttl: 'Approximate allocation', sub: 'Courier parent × service × zone × allocation (.xlsx)', kind: 'xlsx', rows: allocRows, file: `${safeBrand}_Approximate_Allocation.xlsx`, sheet: 'Approximate Allocation' },
    ...(isReviewer ? [{ ttl: 'Allocation — internal view', sub: 'Full CP account codes × priority × allocation % (.xlsx)', kind: 'xlsx', rows: allocationInternalRows(lanes), file: `${safeBrand}_Allocation_Internal.xlsx`, sheet: 'Allocation Internal' }] : []),
    { ttl: 'Serviceability and TAT', sub: 'Zone × weight slab serviceability + transit days (.xlsx)', kind: 'xlsx', rows: svcRows, file: `${safeBrand}_Serviceability_TAT.xlsx`, sheet: 'Serviceability' },
    ...(isReviewer ? [{ ttl: 'Exception & risk report', sub: 'Guardrail-flagged lanes (.xlsx)', kind: 'xlsx', rows: exceptionRows(lanes), file: `${safeBrand}_Exceptions.xlsx`, sheet: 'Exceptions' }] : []),
    ...(isReviewer ? [{ ttl: 'Rate Card Margin Working', sub: 'Margin Working — RTO / Prepaid / COD tabs; editable Final Rate recalculates in Excel (.xlsx)', kind: 'pivot', file: `${safeBrand}_Rate_Card_Margin_Working.xlsx` }] : []),
  ]
  const doDownloadPack = () => {
    if (!hasPack) { enqueueSnackbar('No computed lanes on this version yet — the pack is not ready.', { variant: 'info' }); return }
    const sheets = [{ name: 'Approximate Allocation', rows: allocRows }, { name: 'Serviceability', rows: svcRows }]
    if (isReviewer) {
      sheets.unshift({ name: 'Margin Working', rows: marginWorkingRows(lanes) })
      if (rtoLanes.length) sheets.splice(1, 0, { name: 'Margin Working (RTO)', rows: marginWorkingRows(rtoLanes) })
      sheets.push({ name: 'Allocation Internal', rows: allocationInternalRows(lanes) }, { name: 'Exceptions', rows: exceptionRows(lanes) })
    }
    downloadSheets(`${safeBrand}_Pack.xlsx`, sheets)
  }
  const doCopyTemplate = () => {
    if (!version) return
    navigate(PATHS.newProposal, { state: { seed: versionToSeed(version) } })
    onClose && onClose()
  }

  const doSend = async () => {
    try {
      const res = await send(proposalId).unwrap()
      enqueueSnackbar(res?.clientProposalUri ? 'Shared with client — PDF generated.' : 'Shared with client.', { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.message || 'Share failed.', { variant: 'error' }) }
  }
  const doOutcome = async () => {
    try {
      await outcome({ proposalId, body: { result, reason: reason || undefined } }).unwrap()
      enqueueSnackbar(`Outcome recorded: ${result}.`, { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.message || 'Could not record outcome.', { variant: 'error' }) }
  }
  // Route 3 (v1.2): download the blank CSV template, then upload the filled CSV
  // (multipart) → backend stores it and moves PUSHED → PENDING_L1.
  const doDownloadTemplate = async () => {
    try {
      const blob = await fetchTemplate().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'd2c_order_data_template.csv'
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { enqueueSnackbar(e?.message || 'Could not download template.', { variant: 'error' }) }
  }
  const doUploadOrderData = async (file) => {
    if (!file) return
    try {
      await uploadOrderData({ proposalId, file }).unwrap()
      enqueueSnackbar('Order data uploaded — deal moved to L1 review.', { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.message || 'Could not upload order data.', { variant: 'error' }) }
  }

  const rfiMatrices = useMemo(() => rfiMatricesFromVersion(version), [version])
  const pickupRows = useMemo(() => (version?.pickupLocations || []).map((p, i) => ({
    _k: i, pin: p.pincode, zone: p.zoneTag, vol: (p.monthlyVolume ?? 0).toLocaleString('en-IN'),
  })), [version])
  const pickupCols = cols([
    { field: 'pin', headerName: 'Pickup PIN', minWidth: 110, flex: 0.6, renderCell: ({ row }) => <b>{row.pin}</b> },
    {
      field: 'zone', headerName: 'Zone', minWidth: 100, flex: 0.6,
      renderCell: ({ row }) => {
        const c = ZONE_CLASSES[row.zone] || ZONE_CLASSES.Standard
        return <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1.1, py: 0.3, borderRadius: '10px', bgcolor: c.bg, color: c.color }}>{row.zone || '—'}</Box>
      },
    },
    { field: 'vol', headerName: 'Monthly volume', minWidth: 130, flex: 0.6, align: 'right', headerAlign: 'right', renderCell: ({ row }) => <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{row.vol}</Typography> },
  ])
  // Header verdict — same band-colour language as Step 4 and the Deal Review page.
  const bucketKey = version?.dealBucket ? BUCKET_LABEL_TO_KEY[version.dealBucket] : null
  const bucket = bucketKey ? BUCKETS[bucketKey] : null
  const marginTxt = safeVersion?.dealMarginPct == null ? 'masked' : `${safeVersion.dealMarginPct}%`

  return (
    // Right-hand sidebar (not a centered modal) — history stays visible behind it, and
    // the tall numbered sections read naturally as one scroll. Header + action deck stay
    // pinned at the top, footer actions at the bottom; only the sections scroll.
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100vw', md: 880 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}>
      {/* ── Command header — same gradient band + white action deck as Deal Review ── */}
      <Box sx={{ background: 'linear-gradient(90deg, #103A75, #2065D1)', px: 2.75, py: 2, color: '#fff' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 200 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{proposal ? (proposal.brand || proposalId) : 'Proposal'}</Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
              {proposal?.status && <Pill label={proposal.status} styleKey={proposal.statusKey} />}
              {version?.seq != null && <Pill label={`v${version.seq}`} sx={onBluePill} />}
            </Stack>
          </Box>
          {bucket && (
            <Box>
              <Typography sx={statLbl}>Margin band</Typography>
              <Typography sx={statVal}>{bucket.label.split(' ')[0]} · {marginTxt}</Typography>
              <Typography sx={statSub}>{bucket.range}</Typography>
            </Box>
          )}
          {version?.route != null && (
            <Box>
              <Typography sx={statLbl}>Route</Typography>
              <Typography sx={statVal}>Route {version.route}</Typography>
            </Box>
          )}
          <IconButton size="small" onClick={onClose} sx={{ alignSelf: 'flex-start', ml: 'auto', color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </Box>
      {/* White action deck — SL lifecycle actions grouped by intent (backend enforces
          SL-only access); exactly one "Client PDF" group renders per status. */}
      <Box sx={{ bgcolor: 'background.paper', px: 2.75, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch', borderBottom: '1px solid', borderColor: 'divider' }}>
        {status === 'APPROVED' && (
          <ActionGroup label="Share" tone="decide">
            <Button sx={btnPri} disabled={sendState.isLoading} onClick={doSend}>
              {sendState.isLoading ? 'Sharing…' : '✓  Share with client (generate PDF)'}
            </Button>
          </ActionGroup>
        )}
        <ActionGroup label="Client PDF" tone="rework">
          {DOC_STATUSES.includes(status) ? (
            doc?.ready && doc?.uri
              ? <Button sx={btnBlue} startIcon={<VisibilityOutlinedIcon />} onClick={() => openS3Doc(doc.uri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Open client proposal</Button>
              : <Typography sx={{ fontSize: 12, color: 'text.disabled', alignSelf: 'center' }}>Not generated yet</Typography>
          ) : (
            /* Export Preview (FE Integration Guide v3 §1) — the official doc only exists
               post-decision; this on-demand preview works at every other status, fresh,
               no side effects. */
            <Box component="span" title={!hasPack ? noLanesReason : undefined}>
              <Button sx={btnBlue} startIcon={<VisibilityOutlinedIcon />} disabled={!hasPack}
                onClick={() => exportPreviewPdf(versionId, (m) => enqueueSnackbar(m, { variant: 'error' }))}>
                Export / Preview
              </Button>
            </Box>
          )}
        </ActionGroup>
        {status === 'SENT' && (
          <ActionGroup label="Outcome" tone="send">
            <TextField select size="small" label="Result" value={result} onChange={(e) => setResult(e.target.value)} sx={{ width: 140, bgcolor: '#fff' }}>
              <MenuItem value="CONVERTED">Converted</MenuItem>
              <MenuItem value="LOST">Lost</MenuItem>
            </TextField>
            <TextField size="small" label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ width: 200, bgcolor: '#fff' }} />
            <Button sx={btnBlue} disabled={outcomeState.isLoading} onClick={doOutcome}>
              {outcomeState.isLoading ? 'Saving…' : 'Record outcome'}
            </Button>
          </ActionGroup>
        )}
        {status === 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA' && (
          <ActionGroup label="Order-level data · Route 3" tone="send">
            <Button sx={btnGhost} startIcon={<FileDownloadOutlinedIcon />} disabled={templateState.isLoading} onClick={doDownloadTemplate}>
              {templateState.isLoading ? 'Downloading…' : 'CSV template'}
            </Button>
            <Button sx={btnBlue} component="label" disabled={uploadState.isLoading}>
              {uploadState.isLoading ? 'Uploading…' : 'Upload filled CSV → L1'}
              <input type="file" hidden accept=".csv" onChange={(e) => doUploadOrderData(e.target.files?.[0])} />
            </Button>
          </ActionGroup>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#F4F6FA', p: 2.25 }}>
        {isFetching && <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>Loading this version…</Typography>}
        {isError && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error?.data?.message || error?.message || 'Failed to load this version — the rate card and files can’t be shown.'}</Alert>}

        {/* ── ① What the Sales Leader filled — same bare layout as Deal Review ② ── */}
        <SectionCard title={<SecTitle n={1}>Sales Leader&rsquo;s request</SecTitle>}>
          <ProspectAndCommercialSummary version={safeVersion} proposalRaw={proposal?.raw} showRfi={false} bare />
        </SectionCard>

        {/* ── ② Order-level data — pickups + tinted volume-split matrices ── */}
        <SectionCard title={<SecTitle n={2}>Order-level data (RFI)</SecTitle>} accent="#AEB8C7">
          {!!pickupRows.length && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>Pickup locations</Typography>
              {miniGrid(pickupCols, pickupRows, (r) => r._k)}
            </Box>
          )}
          {rfiMatrices.length ? rfiMatrices.map((m) => <RfiMatrix key={m.serviceType} matrix={m} />) : (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No RFI split captured on this version.</Typography>
          )}
        </SectionCard>

        {/* ── ③ The margin evidence ── */}
        <SectionCard title={<SecTitle n={3}>Margin per weight slab × zone<NoteInfoIcon id="heatmap_aggregate" /></SecTitle>}>
          <RateCardHeatmap data={heatmap} />
          <HeatLegend />
          {noLanesReason && !isFetching && !isError && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>{noLanesReason}</Typography>
          )}
        </SectionCard>

        {/* ── ④ T&Cs ── */}
        <SectionCard title={<SecTitle n={4}>Terms &amp; conditions</SecTitle>} accent="#AEB8C7">
          <List dense sx={{ py: 0 }}>
            {TNC.map((t, i) => (
              <ListItem key={i} sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 26 }}><CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: 12 }} primary={t} />
              </ListItem>
            ))}
          </List>
        </SectionCard>

        {/* ── ⑤ Working files — slim document rows, same language as Deal Review ── */}
        <SectionCard title={<SecTitle n={5}>Working files</SecTitle>} accent="#AEB8C7" sx={{ mb: 0 }}>
          {slFiles.map((f) => {
            const isDoc = f.kind === 'doc'
            const empty = (f.kind === 'xlsx' && !f.rows.length) || (f.kind === 'pivot' && !lanes.length)
            return (
              <Box key={f.ttl} title={f.sub} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: '8px', px: 1.5, py: 1, mb: 1, '&:last-of-type': { mb: 0 }, '&:hover': { bgcolor: '#FAFBFC', borderColor: '#C7D0E0' } }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: isDoc ? '#EAF1FC' : '#ECFDF5' }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 19, color: isDoc ? '#2065D1' : '#15803D' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.3 }}>{f.ttl}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.disabled', lineHeight: 1.3 }}>{isDoc ? 'PDF' : '.xlsx'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', ml: 'auto' }}>
                  {isDoc ? (
                    doc?.ready && doc?.uri
                      ? <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={fileBtnSm} onClick={() => openS3Doc(doc.uri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Download</Button>
                      : <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>not generated yet</Typography>
                  ) : (
                    <Box component="span" title={empty ? noLanesReason : undefined}>
                      <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />} sx={fileBtnSm} disabled={empty}
                        onClick={() => (f.kind === 'pivot'
                          ? downloadRateCardMarginWorking(f.file, lanes, rtoLanes)
                          : (f.extraSheets?.length
                            ? downloadSheets(f.file, [{ name: f.sheet, rows: f.rows }, ...f.extraSheets])
                            : downloadXlsx(f.file, f.rows, f.sheet)))}>Download</Button>
                    </Box>
                  )}
                </Box>
              </Box>
            )
          })}
        </SectionCard>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        {getAccess().includes('PROPOSAL_CREATE') && (
          <Button sx={btnGhost} disabled={!version} onClick={doCopyTemplate}>⧉ Copy as template</Button>
        )}
        <Box component="span" title={!hasPack ? noLanesReason : undefined}>
          <Button sx={btnBlue} startIcon={<FileDownloadOutlinedIcon />} disabled={!hasPack} onClick={doDownloadPack}>Download pack</Button>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="text" sx={btn} onClick={onClose}>Close</Button>
      </Box>
    </Drawer>
  )
}

const btn = { textTransform: 'none', borderRadius: '6px', fontSize: 14 }
const fileBtnSm = { fontSize: 12, fontWeight: 700, py: 0.4, px: 1.25, minWidth: 0, textTransform: 'none', borderRadius: '7px', whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: 0.5, '& svg': { fontSize: 15 } } }
